import { LitElement, css, html, nothing } from 'lit';
import { unsafeHTML } from 'lit-html/directives/unsafe-html.js';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import resetStyle from '../../shared/components/html-content/reset.style';
import { overrideDesignTokens } from '../../shared/styles/override-design-tokens';
import { ludsSpacing } from '../../shared/styles/spacing';
import { ludsBaseFont, ludsTypography, ludsTypographyFontWeight } from '../../shared/styles/typography';
import { CheckedEvent, RadioButtonEvents } from '../../shared/types/radiobutton.model';
import '../icon/icon';
import { RadioButtonOption } from './radiobutton.type';

export type RadioButtonVariants = 'default' | 'toggle';

@customElement('luds-radio-button')
export class LudsRadioButton extends LitElement implements RadioButtonOption {
  /** Specifies whether the radio button is checked */
  @property({ type: Boolean, reflect: true }) checked = false;

  /** Specifies whether the radio button is disabled */
  @property({ type: Boolean, reflect: true }) disabled = false;

  /** Specifies whether the radio button is in an indeterminate state */
  @property({ type: Boolean, reflect: true }) indeterminate = false;

  /** Specifies the label of the radio button */
  @property({ type: String, reflect: true }) label = '';

  /** Specifies whether to use HTML label */
  @property({ type: Boolean, reflect: true }) useHtmlLabel = false;

  /** Specifies the aria-label attribute for A11Y - in case if we don't have label next to radio button */
  @property({ type: String, reflect: true }) radioAriaLabel = '';

  /** Specifies the name attribute of the radio button */
  @property({ type: String, reflect: true }) name = '';

  /** Specifies the ID of the radio button */
  @property({ type: String, reflect: true }) override id = '';

  /** Specifies the value of the radio button */
  @property({ type: String, reflect: true }) value = '';

  //** Specifies the variant / type */
  @property({ type: String, reflect: true }) variant: RadioButtonVariants = 'default';

  static override styles = [
    overrideDesignTokens,
    ludsTypography,
    ludsTypographyFontWeight,
    ludsBaseFont,
    resetStyle,
    ludsSpacing,
    css`
      :host {
        --luds-radio-width: 20px;
        --luds-radio-height: 20px;
        --luds-radio-border-radius: 50%;
        --luds-radio-border-color: var(--strokes-stroke-primary);

        display: inline-block;
        outline: none;
      }

      input[type='radio'] {
        display: none;
      }

      .radio {
        display: flex;
        align-items: center;
        cursor: pointer;
        gap: var(--spacing-spacing-08);
      }

      .custom-radio {
        display: flex;
        align-items: center;
        justify-content: center;
        width: var(--luds-radio-width);
        height: var(--luds-radio-height);
        border: 1px solid var(--luds-radio-border-color);
        border-radius: var(--luds-radio-border-radius);
        flex-shrink: 0;
      }

      .radio-label {
        user-select: none;
        color: var(--semantic-colour-tokens-text-text-primary);
        word-break: break-word;
      }

      .circle {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background-color: var(--semantic-colour-tokens-surface-surface-primary);
      }

      .custom-radio .radio-icon {
        display: none;
      }

      .radio.checked {
        & .custom-radio {
          background-color: var(--semantic-colour-tokens-surface-surface-brand);
          border-color: var(--semantic-colour-tokens-stroke-stroke-brand);
          fill: var(--semantic-colour-tokens-surface-surface-primary);
        }

        & .radio-icon {
          display: block;
        }
      }

      .radio.indeterminate {
        & .custom-radio {
          background-color: var(--semantic-colour-tokens-surface-surface-brand);
          border-color: var(--semantic-colour-tokens-stroke-stroke-brand);
          fill: var(--semantic-colour-tokens-icons-icon-on-dark-surface);
        }

        & .radio-icon-indeterminate {
          display: block;
        }
      }

      .radio.disabled,
      .toggle.disabled {
        cursor: not-allowed;

        & .custom-radio {
          opacity: 42%;
        }

        & .radio-label {
          opacity: 42%;
        }
      }

      .toggle {
        padding: var(--spacing-spacing-08) var(--spacing-spacing-16);
        display: flex;
        align-items: center;
        border-radius: var(--corners-xl);
        border: 1px solid var(--semantic-colour-tokens-stroke-stroke-primary, #c7ccd1);
        transition:
          border-color 0.2s ease-in-out,
          box-shadow 0.2s ease-in-out;

        &:hover {
          cursor: pointer;
          border: 1px solid var(--semantic-colour-tokens-stroke-stroke-dark, #5f6871);
        }

        &:focus-visible {
          outline: none;
          border-color: var(--semantic-colour-tokens-stroke-stroke-brand, #356df1);
          box-shadow: 0 0 4px -1px var(--semantic-colour-tokens-stroke-stroke-brand);
        }
      }

      .toggle-visual {
        display: flex;
        align-items: center;
      }

      .toggle-label {
        line-height: 20px;
      }

      .toggle.checked {
        border-color: var(--semantic-colour-tokens-stroke-stroke-brand);
        fill: var(--semantic-colour-tokens-stroke-stroke-brand);
      }

      .toggle.disabled {
        cursor: not-allowed;
        border-color: color-mix(in srgb, var(--semantic-colour-tokens-stroke-stroke-dark) 42%, #fff);
        color: color-mix(in srgb, var(--semantic-colour-tokens-text-text-primary) 42%, #fff);
        fill: color-mix(in srgb, var(--semantic-colour-tokens-stroke-stroke-brand) 42%, #fff);
      }
    `,
  ];

  private get ariaCheckedState() {
    return this.indeterminate ? 'mixed' : this.checked ? 'true' : 'false';
  }

  private toggleCheck() {
    if (this.disabled) return;
    this.checked = !this.checked;
    this.indeterminate = false;
    this.requestUpdate();
    this.dispatchEvent(
      new CustomEvent(RadioButtonEvents.CHECKED, {
        detail: {
          checked: this.checked,
          disabled: this.disabled,
          indeterminate: this.indeterminate,
          label: this.label,
          radioAriaLabel: this.radioAriaLabel || null,
          name: this.name,
          id: this.id,
          value: this.value,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  private handleKeyDown(event: KeyboardEvent) {
    if (event.code === 'Space') {
      event.preventDefault();
      this.toggleCheck();
    }
  }

  private buildDefaultRadioButton(radioClasses: Record<string, boolean>) {
    return html`<label class="${classMap(radioClasses)}">
      <input
        type="radio"
        id="${this.id}"
        name="${this.name}"
        ?checked="${this.checked}"
        ?disabled="${this.disabled}"
        .indeterminate="${this.indeterminate}"
        value="${this.value}"
        @click="${this.toggleCheck}"
        aria-checked="${this.ariaCheckedState}" />
      <span
        class="custom-radio"
        role="radio"
        aria-checked="${this.indeterminate ? 'mixed' : this.checked}"
        aria-disabled="${this.disabled}"
        aria-label="${ifDefined(this.radioAriaLabel)}"
        aria-labelledby="radio-label"
        tabindex="0">
        ${this.checked && !this.indeterminate ? html`<span class="circle"></span>` : nothing}
        ${this.indeterminate
          ? html`<luds-icon icon="checkbox_mark" size="extra-small" class="checkbox-icon"></luds-icon>`
          : null}
      </span>

      ${this.label
        ? html`<span class="radio-label baseline" id="radio-label">
            ${this.useHtmlLabel ? unsafeHTML(this.label) : this.label}
          </span>`
        : nothing}
    </label>`;
  }

  private buildToggleRadioButton(toggleClasses: Record<string, boolean>) {
    return html`<label>
      <input
        type="radio"
        id="${this.id}"
        name="${this.name}"
        .checked=${this.checked}
        ?disabled=${this.disabled}
        value=${this.value}
        aria-checked="${this.ariaCheckedState}"
        @click=${this.toggleCheck} />
      <span
        class="${classMap(toggleClasses)}"
        role="radio"
        aria-checked="${this.checked}"
        aria-disabled="${this.disabled}"
        aria-label="${ifDefined(this.radioAriaLabel)}"
        aria-labelledby="radio-label"
        tabindex="0"
        @keydown="${this.handleKeyDown}">
        ${this.checked ? html`<luds-icon class="mr-8" icon="completed" size="small"></luds-icon>` : nothing}
        ${this.label
          ? html`
              <span class="baseline toggle-label" id="radio-label">
                ${this.useHtmlLabel ? unsafeHTML(this.label) : this.label}
              </span>
            `
          : nothing}
      </span>
    </label>`;
  }

  override render() {
    const radioClasses = {
      radio: true,
      checked: this.checked,
      disabled: this.disabled,
      indeterminate: this.indeterminate,
    };

    const toggleClasses = {
      toggle: true,
      checked: this.checked,
      disabled: this.disabled,
      indeterminate: this.indeterminate,
    };

    return html`
      ${this.variant === 'default'
        ? this.buildDefaultRadioButton(radioClasses)
        : this.buildToggleRadioButton(toggleClasses)}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'luds-radio-button': LudsRadioButton;
  }

  interface WindowEventMap {
    [RadioButtonEvents.CHECKED]: CustomEvent<CheckedEvent>;
  }
}
